import React, { useEffect, useState } from 'react';
import { BarChart, Bar, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import api from '../api';
import { GOALS_TEXT_SCOPE_STYLES, GOAL_STATUS_COLORS, formatDate, formatNumber, prettyLabel } from './shared';

export default function AdminGoalsDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/goals/dashboard/admin');
        setData(response.data || null);
      } catch {
        setData(null);
      }
    };
    load();
  }, []);

  if (!data) {
    return <div className="goals-text-scope" style={{ padding: 18, background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0' }}><style>{GOALS_TEXT_SCOPE_STYLES}</style>Loading goals dashboard...</div>;
  }

  return (
    <div className="goals-text-scope" style={{ display: 'grid', gap: 16 }}>
      <style>{GOALS_TEXT_SCOPE_STYLES}</style>
      <div style={{ background: 'linear-gradient(135deg,#f8fbff 0%,#ffffff 100%)', border: '1px solid #dbe5f1', borderRadius: 20, padding: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '0.08em', color: '#1d4ed8' }}>EXECUTIVE VIEW</div>
        <div style={{ marginTop: 8, fontSize: 24, fontWeight: 900, color: '#0f172a' }}>Supervisory visibility across production, QC, sales, HR, inventory, accounts, and admin</div>
        <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6, color: '#334155' }}>
          This dashboard is intentionally different from module workspaces. It focuses on risk, performance movement, and cross-module governance rather than daily task execution controls.
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        {[['Overdue Goals', data.overdueCount, '#dc2626'], ['At-Risk Goals', data.atRiskCount, '#ea580c'], ['Completed This Month', data.completedThisMonth, '#16a34a']].map(([label, value, color]) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 18 }}>
            <div style={{ fontSize: 13, color: '#000', fontWeight: 700 }}>{label}</div>
            <div style={{ marginTop: 8, fontSize: 32, fontWeight: 900, color }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 16 }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 18 }}>
          <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>Goals by Status</div>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.statusBreakdown || []} dataKey="count" nameKey="status" outerRadius={90} label>
                  {(data.statusBreakdown || []).map((entry) => <Cell key={entry.status} fill={(GOAL_STATUS_COLORS[entry.status] || GOAL_STATUS_COLORS.ACTIVE).text} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 18 }}>
          <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>Module-wise Avg Progress</div>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.moduleProgress || []}>
                <XAxis dataKey="module" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="avg_completion" fill="#2563eb" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 18 }}>
          <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>Top At-Risk Goals</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {(data.atRiskGoals || []).length ? data.atRiskGoals.map((goal) => (
              <div key={goal.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
                <div style={{ fontWeight: 700 }}>{goal.title}</div>
                <div style={{ fontSize: 13, color: '#000' }}>{prettyLabel(goal.assigned_module)} • {formatNumber(goal.completion_percentage)}% • Due {formatDate(goal.end_date)}</div>
              </div>
            )) : <div style={{ color: '#000' }}>No at-risk goals.</div>}
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 18 }}>
          <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>Recent Updates</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {(data.recentUpdates || []).length ? data.recentUpdates.map((item) => (
              <div key={item.id} style={{ borderLeft: '4px solid #2563eb', paddingLeft: 12 }}>
                <div style={{ fontWeight: 700 }}>{item.goal_title}</div>
                <div style={{ fontSize: 13, color: '#000' }}>{formatDate(item.update_date)} • {formatNumber(item.progress_percentage)}%</div>
              </div>
            )) : <div style={{ color: '#000' }}>No updates yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
